/**
 * Level 17: Iron Fortress
 * 
 * An imposing metallic fortress with armored shield enemies.
 * Heavy industrial theme with lava moats, moving platforms,
 * and shield enemies that require strategic approach.
 * 
 * Design philosophy: Tactical combat focus with clear enemy patterns
 * encouraging player to observe and time their attacks.
 * 
 * @module configs/levels/Level17_IronFortress
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level17_IronFortress = {
    id: 17,
    name: 'Iron Fortress',
    subtitle: 'Steel & Fire',
    description: 'Storm the fortress guarded by armored shield enemies.',

    worldWidth: 5400,
    worldHeight: 2000,
    playerStart: { x: 150, y: 1700 },
    gravity: 1.05,

    theme: {
        bgGradient: ['#1a1a2e', '#2a1a1a'],
        platformColor: 0x4a4a5a,
        accentColor: 0xff6600,
        particleColor: 0xffaa00,
        ambient: 'fortress'
    },

    platforms: [
        // Entry courtyard
        { x: 0, y: 1800, w: 500, h: 60 },
        { x: 600, y: 1650, w: 200, h: 30 },
        { x: 900, y: 1500, w: 250, h: 35 },

        // Drawbridge section
        { x: 1200, y: 1700, w: 180, h: 30 },
        { x: 1500, y: 1550, w: 200, h: 30 },
        { x: 1800, y: 1400, w: 250, h: 35 },

        // Inner keep - wide platforms for shield enemy encounters
        { x: 2100, y: 1600, w: 400, h: 50 },
        { x: 2350, y: 1400, w: 300, h: 40 },

        // Armory climb
        { x: 2700, y: 1250, w: 180, h: 25 },
        { x: 2500, y: 1100, w: 160, h: 25 },
        { x: 2800, y: 950, w: 200, h: 30 },

        // Battlements (wide walkway)
        { x: 3100, y: 1150, w: 350, h: 45 },
        { x: 3500, y: 1000, w: 250, h: 35 },

        // Tower ascent
        { x: 3800, y: 1200, w: 200, h: 30 },
        { x: 3700, y: 1000, w: 150, h: 25 },
        { x: 3900, y: 800, w: 200, h: 30 },

        // Throne room
        { x: 4200, y: 950, w: 300, h: 45 },
        { x: 4550, y: 800, w: 250, h: 35 },
        { x: 4850, y: 1000, w: 350, h: 50 },
        { x: 5100, y: 1200, w: 300, h: 50 }
    ],

    movingPlatforms: [
        // Drawbridge platforms
        { x: 1050, y: 1750, w: 120, h: 22, moveX: 140, speed: 0.003 },
        { x: 1350, y: 1620, w: 130, h: 22, moveY: 120, speed: 0.0025 },
        // Armory elevator
        { x: 2600, y: 1170, w: 140, h: 22, moveY: 180, speed: 0.002 },
        // Tower platforms
        { x: 3850, y: 1100, w: 110, h: 20, moveX: 100, speed: 0.003 },
        { x: 4100, y: 880, w: 120, h: 20, moveY: 100, speed: 0.0035 }
    ],

    spikes: [
        { x: 1250, y: 1700 },
        { x: 1300, y: 1700 },
        { x: 2150, y: 1600 },
        { x: 2200, y: 1600 },
        { x: 3150, y: 1150 },
        { x: 3200, y: 1150 },
        { x: 4900, y: 1000 },
        { x: 4950, y: 1000 },
        { x: 5150, y: 1200 },
        { x: 5200, y: 1200 }
    ],

    stars: [
        { x: 900, y: 1400 },
        { x: 1800, y: 1300 },
        { x: 2500, y: 1000 },
        { x: 3500, y: 900 },
        { x: 3900, y: 700 },
        { x: 5000, y: 900 }
    ],

    enemies: [
        // Regular patrols
        { x: 650, y: 1600 },
        { x: 3200, y: 1100 },
        // Shield enemies (new!) - wide platforms for interesting combat
        { x: 2200, y: 1550, type: 'shield', options: { speed: 1.0, walkDist: 130, slamInterval: 2800 } },
        { x: 2400, y: 1350, type: 'shield', options: { speed: 1.3, walkDist: 100, slamInterval: 2500 } },
        { x: 3400, y: 950, type: 'shield', options: { speed: 1.5, walkDist: 80, slamInterval: 2000 } },
        { x: 4600, y: 750, type: 'shield', options: { speed: 1.0, walkDist: 90, slamInterval: 3000, shieldColor: 0xff6600 } },
        // Flying scout
        { x: 2850, y: 800, type: 'flying', options: { amplitude: 60, speed: 1.3, walkDist: 100 } }
    ],

    door: { x: 5300, y: 1050, w: 60, h: 300 },
    button: { x: 5100, y: 1195 },
    box: { x: 4300, y: 850, floating: true },
    goal: { x: 5350, y: 1100 },

    lavaZones: [
        // Lava moat around fortress
        { x: 1100, y: 1850, w: 400, h: 150 },
        // Inner furnace
        { x: 2700, y: 1700, w: 300, h: 300 }
    ],

    trampolines: [
        { x: 1150, y: 1700, w: 80 },
        { x: 2650, y: 1250, w: 70 },
        { x: 4150, y: 950, w: 70 }
    ],

    portals: [
        { x1: 450, y1: 1750, x2: 3050, y2: 1100, color: 0xff6600 }
    ],

    speedrunTargets: { gold: 65, silver: 85, bronze: 115 }
};

export default Level17_IronFortress;
